import { useCallback, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { IInsurancePolicy } from '@/@types/insurance';

export function usePolicySelection(filteredPolicies: IInsurancePolicy[]) {
  const [selectedIds, setSelectedIds] = useState<Set<IInsurancePolicy['_id']>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const batchRemove = useMutation(api.insurance.policies.batchRemove);

  const handleSelectRow = useCallback((policyId: IInsurancePolicy['_id'], selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(policyId);
      } else {
        next.delete(policyId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(filteredPolicies.map(p => p._id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [filteredPolicies]
  );

  const handleDeleteConfirm = useCallback(async () => {
    await batchRemove({ policyIds: Array.from(selectedIds) });
    setSelectedIds(new Set());
    setIsDeleteOpen(false);
  }, [selectedIds, batchRemove]);

  const isAllSelected = filteredPolicies.length > 0 && selectedIds.size === filteredPolicies.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  return {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
  };
}
